import { useEffect, useState } from 'react';
import {
  DocumentSkelton,
  DocumentSourceType,
} from 'api/document/types/document';
import { Space, SpaceTabSkelton } from 'api/space';
import { SpaceTabDocumentType, SpaceTabType } from 'api/space/space-tab';
import { TemplateId, TemplateObjectType } from 'data/templates/api';
import {
  useActiveTemplateId,
  useProjectTemplateSpaces,
} from 'data/templates/projects';
import { useTemplatesQuery } from 'queries';
import { DocumentTemplate } from '../types';

const DOCUMENT_TEMPLATE_SPACE_ID_FILLER = -999;

export interface MentionsDocumentTemplate
  extends Omit<
    DocumentSkelton,
    | 'accountId'
    | 'documentId'
    | 'commentThreadId'
    | 'projectId'
    | 'version'
    | 'versioningQueued'
  > {
  private?: boolean;
  documentId: TemplateId;
  title: string;
  source?: {
    sourceId: number;
    sourceType: DocumentSourceType;
  };
}

const transformDocumentTemplatesToSpaceTabDocuments = (
  templates: DocumentTemplate[],
) => {
  // docs with -999 as the spaceId are document templates
  return templates.map((template) => {
    return {
      spaceTabId: template.templateId as number,
      spaceTabTitle: template.templateName as string,
      spaceTabType: SpaceTabDocumentType.RocketlaneDocument,
      space: {
        spaceId: DOCUMENT_TEMPLATE_SPACE_ID_FILLER,
      } as Space,
      type: SpaceTabType.DocumentTemplate,
    };
  });
};

const transformSpaceTabDocumentTemplates = (spaces, templateId, documents) => {
  let privateSpaceTabs = [] as SpaceTabSkelton[];
  const publicSpaceTabs = spaces.reduce((acc, space) => {
    const spaceTabs = space.spaceTabs
      .map((tab) => {
        return {
          ...tab,
          space: {
            spaceId: space.spaceId,
          },
          project: {
            projectId: templateId,
          },
          type: SpaceTabType.ProjectTemplate,
        };
      })
      .filter(
        (tab) =>
          // filter out tabs which were duplicates of document templates
          !documents.some(
            (documentTemplate) =>
              tab.url === `/templates/${documentTemplate.spaceTabId}`,
          ),
      );

    if (!space.private) {
      return [...acc, ...spaceTabs];
    }

    privateSpaceTabs = [...privateSpaceTabs, ...spaceTabs];

    return acc;
  }, [] as SpaceTabSkelton[]);

  return {
    publicSpaceTabs,
    privateSpaceTabs,
  };
};

export const useGetAllDocumentTemplates = (): {
  documents: SpaceTabSkelton[];
  publicSpaceTabs: SpaceTabSkelton[];
  privateSpaceTabs: SpaceTabSkelton[];
  isLoading: boolean;
} => {
  const [documents, setDocuments] = useState<SpaceTabSkelton[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const templateId = useActiveTemplateId();
  const spaces = Object.values(useProjectTemplateSpaces());
  const { publicSpaceTabs, privateSpaceTabs } =
    transformSpaceTabDocumentTemplates(spaces, templateId, documents);

  const { data: templates } = useTemplatesQuery();

  const documentTemplates = templates?.filter(
    (template) => template.objectType === TemplateObjectType.Document,
  );

  useEffect(() => {
    if (documentTemplates && documentTemplates.length > 0 && isLoading) {
      setDocuments(
        transformDocumentTemplatesToSpaceTabDocuments(
          documentTemplates as DocumentTemplate[],
        ),
      );
      setIsLoading(false);
    }
  }, [documentTemplates, isLoading]);

  return {
    documents,
    isLoading,
    publicSpaceTabs,
    privateSpaceTabs,
  };
};
